import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import { DialogTextActionButton } from "../../Base/DialogTextActionButton";
import { useAppSelector } from "../../../store";
import { getTournoi } from "../../../features/tournoi/tournoiSlice";
import { useLocation } from "react-router-dom";
import {
  listTournamentPayments,
  refundOrphanTournamentPayments,
  refundTournamentPayments,
} from "../../../services/firebaseCloudFunctions";
import {
  REFUND_CONFIRMATION_TOKEN,
  OrphanScanPeriod,
  RefundResultItem,
  RefundTournamentPaymentsResult,
  TournamentStripePayment,
} from "../../../types/TournamentPayment";

const formatEuros = (amountEuros: number) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(amountEuros);

const refundStatusLabel = (status: TournamentStripePayment["refundStatus"]) => {
  switch (status) {
    case "eligible":
      return "À rembourser";
    case "refunded":
      return "Remboursé";
    case "partially_refunded":
      return "Partiellement remboursé";
    default:
      return status;
  }
};

const refundStatusColor = (
  status: TournamentStripePayment["refundStatus"]
): "default" | "success" | "warning" | "info" => {
  switch (status) {
    case "eligible":
      return "warning";
    case "refunded":
      return "success";
    case "partially_refunded":
      return "info";
    default:
      return "default";
  }
};

const paymentSourceLabel = (source?: TournamentStripePayment["paymentSource"]) => {
  if (source === "admin") return "Admin (lié)";
  if (source === "registration") return "Inscription";
  return null;
};

type RefundFlow = "linked" | "orphan";

interface RefundSectionProps {
  title: string;
  description: React.ReactNode;
  payments: TournamentStripePayment[];
  selectedIds: Set<string>;
  onToggleAll: (checked: boolean) => void;
  onToggleOne: (paymentId: string, checked: boolean) => void;
  onRefundClick: () => void;
  refunding: boolean;
  showPaymentSource?: boolean;
  showPaymentId?: boolean;
  emptyMessage: string;
}

const RefundSection: FC<RefundSectionProps> = ({
  title,
  description,
  payments,
  selectedIds,
  onToggleAll,
  onToggleOne,
  onRefundClick,
  refunding,
  showPaymentSource = false,
  showPaymentId = false,
  emptyMessage,
}) => {
  const eligiblePayments = useMemo(
    () => payments.filter((p) => p.selectable),
    [payments]
  );

  const selectedPayments = useMemo(
    () => payments.filter((p) => selectedIds.has(p.paymentId)),
    [payments, selectedIds]
  );

  const selectedTotalEuros = useMemo(
    () => selectedPayments.reduce((sum, p) => sum + p.amountEuros, 0),
    [selectedPayments]
  );

  const allEligibleSelected =
    eligiblePayments.length > 0 &&
    eligiblePayments.every((p) => selectedIds.has(p.paymentId));

  const someEligibleSelected = eligiblePayments.some((p) =>
    selectedIds.has(p.paymentId)
  );

  const totalEligibleEuros = eligiblePayments.reduce(
    (sum, p) => sum + p.amountEuros,
    0
  );

  if (payments.length === 0) {
    return (
      <Stack spacing={1}>
        <Typography variant="subtitle1">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
        <Alert severity="info" sx={{ maxWidth: 720 }}>
          {emptyMessage}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
        {description}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {eligiblePayments.length} paiement(s) éligible(s) — Total :{" "}
        {formatEuros(totalEligibleEuros)}
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={someEligibleSelected && !allEligibleSelected}
                  checked={allEligibleSelected}
                  onChange={(e) => onToggleAll(e.target.checked)}
                  disabled={eligiblePayments.length === 0 || refunding}
                  inputProps={{ "aria-label": "Tout sélectionner" }}
                />
              </TableCell>
              <TableCell>Joueur</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell align="right">Montant</TableCell>
              <TableCell align="center">Tableaux</TableCell>
              {showPaymentSource && <TableCell>Origine</TableCell>}
              {showPaymentId && <TableCell>Paiement</TableCell>}
              <TableCell>Date</TableCell>
              <TableCell>Statut</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment) => {
              const isSelected = selectedIds.has(payment.paymentId);
              const sourceLabel = paymentSourceLabel(payment.paymentSource);
              return (
                <TableRow
                  key={payment.paymentId}
                  hover={payment.selectable}
                  selected={isSelected && payment.selectable}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) =>
                        onToggleOne(payment.paymentId, e.target.checked)
                      }
                      disabled={!payment.selectable || refunding}
                      inputProps={{
                        "aria-label": `Sélectionner ${payment.playerName}`,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{payment.playerName}</Typography>
                    {payment.playerLicenceId && (
                      <Typography variant="caption" color="text.secondary">
                        {payment.playerLicenceId}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{payment.email || "—"}</TableCell>
                  <TableCell align="right">
                    {formatEuros(payment.amountEuros)}
                  </TableCell>
                  <TableCell align="center">{payment.boardCount}</TableCell>
                  {showPaymentSource && (
                    <TableCell>{sourceLabel || "—"}</TableCell>
                  )}
                  {showPaymentId && (
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace">
                        {payment.paymentId}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    {payment.paidAt
                      ? new Date(payment.paidAt).toLocaleString("fr-FR")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={refundStatusLabel(payment.refundStatus)}
                      color={refundStatusColor(payment.refundStatus)}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Typography variant="body2">
          {selectedIds.size} paiement(s) sélectionné(s) — Total :{" "}
          <strong>{formatEuros(selectedTotalEuros)}</strong>
        </Typography>
        <Button
          variant="contained"
          color="error"
          onClick={onRefundClick}
          disabled={selectedIds.size === 0 || refunding}
        >
          Rembourser la sélection
        </Button>
      </Stack>
    </Stack>
  );
};

const RefundResultsTable: FC<{ results: RefundResultItem[]; title: string }> = ({
  results,
  title,
}) => (
  <Box>
    <Typography variant="subtitle2" gutterBottom>
      {title}
    </Typography>
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Paiement</TableCell>
            <TableCell>E-mail</TableCell>
            <TableCell align="right">Montant</TableCell>
            <TableCell>Statut</TableCell>
            <TableCell>Détail</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map((result) => (
            <TableRow key={result.paymentId}>
              <TableCell>
                <Typography variant="caption" fontFamily="monospace">
                  {result.paymentId}
                </Typography>
              </TableCell>
              <TableCell>{result.email || "—"}</TableCell>
              <TableCell align="right">
                {result.amountCents != null
                  ? formatEuros(result.amountCents / 100)
                  : "—"}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={
                    result.status === "succeeded"
                      ? "Réussi"
                      : result.status === "already_refunded"
                        ? "Déjà remboursé"
                        : "Échec"
                  }
                  color={result.status === "failed" ? "error" : "success"}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                {result.error ||
                  (result.stripeRefundId
                    ? `Refund ${result.stripeRefundId}`
                    : "—")}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const AdminTournamentRefunds: FC = () => {
  const tournoi = useAppSelector(getTournoi);
  const location = useLocation();
  const tournamentId = tournoi?.id as string | undefined;

  const [linkedPayments, setLinkedPayments] = useState<TournamentStripePayment[]>(
    []
  );
  const [orphanPayments, setOrphanPayments] = useState<TournamentStripePayment[]>(
    []
  );
  const [orphanScanPeriod, setOrphanScanPeriod] =
    useState<OrphanScanPeriod | null>(null);
  const [linkedSelectedIds, setLinkedSelectedIds] = useState<Set<string>>(
    new Set()
  );
  const [orphanSelectedIds, setOrphanSelectedIds] = useState<Set<string>>(
    new Set()
  );
  const [loading, setLoading] = useState(false);
  const [refundingFlow, setRefundingFlow] = useState<RefundFlow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmStep1Open, setConfirmStep1Open] = useState(false);
  const [confirmStep2Open, setConfirmStep2Open] = useState(false);
  const [activeFlow, setActiveFlow] = useState<RefundFlow | null>(null);
  const [confirmationInput, setConfirmationInput] = useState("");
  const [lastLinkedResults, setLastLinkedResults] = useState<
    RefundResultItem[] | null
  >(null);
  const [lastOrphanResults, setLastOrphanResults] = useState<
    RefundResultItem[] | null
  >(null);

  const defaultSelection = (items: TournamentStripePayment[]) =>
    new Set(items.filter((p) => p.selectable).map((p) => p.paymentId));

  const loadPayments = useCallback(async () => {
    if (!tournamentId) return;
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await listTournamentPayments({ tournamentId });
      const linked = response.data.linked ?? [];
      const orphan = response.data.orphan ?? [];
      setLinkedPayments(linked);
      setOrphanPayments(orphan);
      setOrphanScanPeriod(response.data.orphanScanPeriod ?? null);
      setLinkedSelectedIds(defaultSelection(linked));
      setOrphanSelectedIds(defaultSelection(orphan));
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Impossible de charger les paiements Stripe.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    void loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    if (location.hash === "#remboursements") {
      document.getElementById("remboursements")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [location.hash, linkedPayments.length, orphanPayments.length]);

  const activeSelectedIds =
    activeFlow === "linked" ? linkedSelectedIds : orphanSelectedIds;

  const activePayments =
    activeFlow === "linked" ? linkedPayments : orphanPayments;

  const selectedActivePayments = useMemo(
    () => activePayments.filter((p) => activeSelectedIds.has(p.paymentId)),
    [activePayments, activeSelectedIds]
  );

  const selectedActiveTotal = useMemo(
    () => selectedActivePayments.reduce((sum, p) => sum + p.amountEuros, 0),
    [selectedActivePayments]
  );

  const makeToggleHandlers = (
    payments: TournamentStripePayment[],
    setSelected: React.Dispatch<React.SetStateAction<Set<string>>>
  ) => {
    const eligible = payments.filter((p) => p.selectable);
    return {
      toggleAll: (checked: boolean) => {
        setSelected(
          checked
            ? new Set(eligible.map((p) => p.paymentId))
            : new Set()
        );
      },
      toggleOne: (paymentId: string, checked: boolean) => {
        setSelected((prev) => {
          const next = new Set(prev);
          if (checked) next.add(paymentId);
          else next.delete(paymentId);
          return next;
        });
      },
    };
  };

  const linkedHandlers = makeToggleHandlers(
    linkedPayments,
    setLinkedSelectedIds
  );
  const orphanHandlers = makeToggleHandlers(
    orphanPayments,
    setOrphanSelectedIds
  );

  const handleOpenRefundFlow = (flow: RefundFlow) => {
    const selected =
      flow === "linked" ? linkedSelectedIds : orphanSelectedIds;
    if (selected.size === 0) return;
    setActiveFlow(flow);
    setConfirmationInput("");
    setConfirmStep1Open(true);
  };

  const applyRefundResult = (
    result: RefundTournamentPaymentsResult,
    flow: RefundFlow
  ) => {
    if (flow === "linked") {
      setLastLinkedResults(result.refunds);
    } else {
      setLastOrphanResults(result.refunds);
    }

    if (result.status === "completed") {
      setSuccessMessage(
        `${result.succeededCount} remboursement(s) effectué(s) avec succès.`
      );
    } else if (result.status === "partial_failure") {
      setError(
        `${result.succeededCount} remboursement(s) réussi(s), ${result.failedCount} échec(s). Consultez le détail ci-dessous.`
      );
    } else {
      setError("Aucun remboursement n'a pu être effectué.");
    }
  };

  const handleRefund = async () => {
    if (!tournamentId || !activeFlow || activeSelectedIds.size === 0) return;
    setRefundingFlow(activeFlow);
    setError(null);
    setSuccessMessage(null);
    setConfirmStep2Open(false);
    try {
      const payload = {
        tournamentId,
        paymentIds: Array.from(activeSelectedIds),
        confirmationToken: confirmationInput.trim(),
      };
      const response =
        activeFlow === "linked"
          ? await refundTournamentPayments(payload)
          : await refundOrphanTournamentPayments(payload);
      applyRefundResult(response.data, activeFlow);
      await loadPayments();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Erreur lors du remboursement.";
      setError(message);
    } finally {
      setRefundingFlow(null);
    }
  };

  const refunding = refundingFlow !== null;
  const confirmTitle =
    activeFlow === "linked"
      ? "Confirmer le remboursement des inscriptions liées"
      : "Confirmer le remboursement des paiements non rattachés";

  return (
    <Stack spacing={3} sx={{ mt: 1 }} id="remboursements">
      <Typography variant="h6">Remboursements Stripe</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
        Deux flux distincts : inscriptions liées à un paiement Stripe, et
        paiements Stripe orphelins (ex. lien mail sans rattachement). Par
        défaut, tous les paiements éligibles sont cochés — décochez ceux à
        exclure. Double confirmation requise pour chaque flux.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Button
        variant="outlined"
        onClick={() => void loadPayments()}
        disabled={loading || refunding}
        startIcon={loading ? <CircularProgress size={16} /> : null}
        sx={{ alignSelf: "flex-start" }}
      >
        Actualiser les listes
      </Button>

      {loading && linkedPayments.length === 0 && orphanPayments.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <RefundSection
            title="Inscriptions liées à Stripe"
            description="Paiements rattachés à une participation via paymentId. Le remboursement met à jour le statut des inscriptions concernées."
            payments={linkedPayments}
            selectedIds={linkedSelectedIds}
            onToggleAll={linkedHandlers.toggleAll}
            onToggleOne={linkedHandlers.toggleOne}
            onRefundClick={() => handleOpenRefundFlow("linked")}
            refunding={refundingFlow === "linked"}
            showPaymentSource
            emptyMessage="Aucune inscription liée à un paiement Stripe."
          />

          <Divider />

          <RefundSection
            title="Paiements Stripe non rattachés"
            description={
              <>
                Paiements Stripe sans <code>paymentId</code> sur une
                participation, repérés entre la première et la dernière
                inscription du tournoi (+ 60 jours de marge pour les liens
                mail). Si aucune inscription, repli sur les dates d&apos;ouverture
                / fermeture ou les tableaux. Les paiements sans metadata ne
                sont retenus que si l&apos;e-mail correspond à un inscrit.{" "}
                <strong>
                  Ces remboursements n&apos;affectent pas le statut des
                  inscriptions.
                </strong>
                {orphanScanPeriod && (
                  <>
                    {" "}
                    Période analysée : du{" "}
                    {new Date(orphanScanPeriod.start).toLocaleString("fr-FR")}{" "}
                    au{" "}
                    {new Date(orphanScanPeriod.end).toLocaleString("fr-FR")}.
                  </>
                )}
              </>
            }
            payments={orphanPayments}
            selectedIds={orphanSelectedIds}
            onToggleAll={orphanHandlers.toggleAll}
            onToggleOne={orphanHandlers.toggleOne}
            onRefundClick={() => handleOpenRefundFlow("orphan")}
            refunding={refundingFlow === "orphan"}
            showPaymentId
            emptyMessage="Aucun paiement Stripe orphelin pour ce tournoi."
          />
        </>
      )}

      {lastLinkedResults && lastLinkedResults.length > 0 && (
        <RefundResultsTable
          title="Résultat — inscriptions liées"
          results={lastLinkedResults}
        />
      )}
      {lastOrphanResults && lastOrphanResults.length > 0 && (
        <RefundResultsTable
          title="Résultat — paiements non rattachés"
          results={lastOrphanResults}
        />
      )}

      <Dialog
        open={confirmStep1Open}
        onClose={() => setConfirmStep1Open(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography paragraph>
              Vous allez rembourser{" "}
              <strong>{activeSelectedIds.size} paiement(s)</strong> pour un total
              de <strong>{formatEuros(selectedActiveTotal)}</strong>.
            </Typography>
            {activeFlow === "orphan" && (
              <Typography paragraph color="warning.main">
                Les inscriptions ne seront pas modifiées automatiquement.
              </Typography>
            )}
            <Typography paragraph color="error">
              Cette action est irréversible. Les fonds seront recrédités via
              Stripe.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Paiements concernés :
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              {selectedActivePayments.map((p) => (
                <li key={p.paymentId}>
                  {p.playerName} — {formatEuros(p.amountEuros)}
                </li>
              ))}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <DialogTextActionButton onClick={() => setConfirmStep1Open(false)}>
            Annuler
          </DialogTextActionButton>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setConfirmStep1Open(false);
              setConfirmationInput("");
              setConfirmStep2Open(true);
            }}
          >
            Continuer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmStep2Open}
        onClose={() => setConfirmStep2Open(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Validation finale</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Pour confirmer, saisissez{" "}
            <strong>{REFUND_CONFIRMATION_TOKEN}</strong> ci-dessous.
          </DialogContentText>
          <TextField
            fullWidth
            label="Confirmation"
            value={confirmationInput}
            onChange={(e) => setConfirmationInput(e.target.value)}
            autoFocus
            disabled={refunding}
          />
        </DialogContent>
        <DialogActions>
          <DialogTextActionButton
            onClick={() => setConfirmStep2Open(false)}
            disabled={refunding}
          >
            Annuler
          </DialogTextActionButton>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleRefund()}
            disabled={
              confirmationInput.trim() !== REFUND_CONFIRMATION_TOKEN ||
              refunding
            }
            startIcon={refunding ? <CircularProgress size={16} /> : null}
          >
            {refunding ? "Remboursement..." : "Confirmer le remboursement"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AdminTournamentRefunds;
